import { privateImageCache } from '@ff-os/rendering-react/asset-loading/privateImageCache';
import { createImageUploadQueue, isMemoryHeavyImage } from '@ff-os/shared-utils/createImageUploadQueue';
import { createAuthorizationHeader, createDescriptorIfPrivate } from '@ff-os/shared-utils/private-urls';
import { PrivateAssetResult } from '@ff-os/shared-utils/types/private-assets/PrivateAssetResult';
import { PrivateAssetUploadAction } from '@ff-os/shared-utils/types/private-assets/PrivateAssetUploadAction';
import { urls } from '@ff-os/shared-utils/urls';
import { withRetries } from '@ff-os/shared-utils/withRetries';
import fetch from 'cross-fetch';
import { PdpState } from '../../store';
import { withAuthorization } from '../authorization/withAuthorization';
import { createAsyncAppThunk } from '../createAsyncAppThunk';

const queue = createImageUploadQueue({ maxParallelLightUploads: 8, maxParallelHeavyUploads: 1 });

export const uploadPrivateAsset = createAsyncAppThunk<PrivateAssetUploadAction<any>, PrivateAssetResult<any>>(
    'privateAssets/upload', async (action, thunkApi) => {

        return withRetries({ retries: 3 }, async () => {
            return await queue.enqueue(
                async () => {

                    const response = await withAuthorization(async (state: PdpState) => {
                        return fetch(urls.uploadPrivateAsset, await formatRequest(action.upload, state));
                    }, thunkApi);

                    if (!response.ok) {
                        throw new Error(`Upload error: ${urls.uploadPrivateAsset} [${response.status}]`);
                    } else {

                        const result = await response.json() as PrivateAssetResult<any>;

                        const url = urls.privateAssetUrl(result.assetId);
                        const descriptor = createDescriptorIfPrivate(url, thunkApi.getState().auth.jwt);

                        const entry = privateImageCache.require(descriptor);
                        await entry.promise;

                        return result;
                    }
                },
                isMemoryHeavyImage(action.context.imageWidth, action.context.imageHeight)
            );
        });
    });

const formatRequest = async (upload: { file: File; type: string }, state: PdpState) => {

    const formData = new FormData();
    formData.append('file', upload.file);
    formData.append('type', upload.type);

    return {
        method: 'POST',
        headers: createAuthorizationHeader(state.auth.jwt!),
        body: formData
    };
};